import type { DeepReadonly, ToRefs, Ref } from 'vue'
import { reactive, readonly, toRefs } from 'vue'

import type { Folder } from '@/entities/folder'
import type { ShelfFile } from '@/entities/shelfFile'
import type { FileValidator } from '~/lib/validators'
import type { CreateFilePayload } from '~/lib/file-formatter'
import type { EditDeck } from '@/composables/useEditDeck'
import { imageValidator } from '~/lib/validators'
import { serializeFile } from '~/lib/file-formatter'

interface State {
  loading: boolean
  error: Error | null
  folder: Folder | null
  file: File | null
}

export class FileRequiredError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FileRequiredError'
  }
}

export class FolderRequiredError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FolderRequiredError'
  }
}

export interface UseShelf extends DeepReadonly<ToRefs<State>> {
  setPreviewFile(file: File | null): Promise<void>
  selectFile(shelfFile: ShelfFile): Promise<void>
  saveFileEdits(viewAccessType: 'private' | 'selected' | 'public'): Promise<ShelfFile | void>
  refetch(): Promise<void>
}

export function useShelf({ deck }: { deck: DeepReadonly<Ref<EditDeck>> }): UseShelf {
  const state = reactive<State>({
    loading: false,
    error: null,
    file: null,
    folder: null,
  })

  async function refetch(): Promise<void> {
    state.error = null

    try {
      const fileId = deck.value.file_id
      const shelfFile = fileId ? await _getShelfFile(fileId) : null

      state.file = shelfFile ? await _imageToFile(shelfFile) : null
      state.folder = await _getFolderOrDefault(deck.value.company_id, shelfFile?.folder_id)
    } catch (e) {
      if (e instanceof Error) {
        state.error = e
      }
    }
  }

  async function setPreviewFile(file: File | null, validator: FileValidator = imageValidator): Promise<void> {
    state.error = null
    try {
      await validator.validate(file)
      state.file = file
    } catch (e) {
      if (e instanceof Error) {
        state.error = e
      }
    }
  }

  async function selectFile(shelfFile: ShelfFile): Promise<void> {
    state.error = null
    try {
      state.file = await _imageToFile(shelfFile)
    } catch (e) {
      if (e instanceof Error) {
        state.error = e
      }
    }
  }

  async function saveFileEdits(viewAccessType: 'private' | 'selected' | 'public'): Promise<ShelfFile | void> {
    state.error = null
    state.loading = true
    try {
      if (!state.file) {
        if (viewAccessType !== 'public') return
        throw new FileRequiredError('A file is required to make edits.')
      }
      if (!state.folder) {
        if (viewAccessType !== 'public') return
        throw new FolderRequiredError('A folder is required to make edits.')
      }

      return await _createFile({
        companyId: deck.value.company_id,
        folderId: state.folder.id,
        description: deck.value.description,
        newFile: state.file,
      })
    } catch (e) {
      if (e instanceof Error) {
        state.error = e
      }
    } finally {
      state.loading = false
    }
  }

  async function _imageToFile(originalFile: ShelfFile): Promise<File> {
    const res = await fetch(originalFile.url as string)
    const blob = await res.blob()
    const file = new File([blob], originalFile.mimetype, { type: blob.type })

    return file
  }

  async function _createFile(payload: CreateFilePayload): Promise<ShelfFile> {
    const serializedFile = await serializeFile(payload)
    const data = await $fetch<any>(`v2/companies/${payload.companyId}/shelf/files`, {
      method: 'POST',
      body: serializedFile,
      ...useFetchSharedOptions(),
    })

    return data
  }

  async function _getShelfFile(fileId: number): Promise<ShelfFile> {
    const data = await $fetch<any>(`v2/shelf/files/${fileId}`, { ...useFetchSharedOptions() })
    return data
  }

  async function _getFolderOrDefault(companyId: number, folderId: number | null | undefined) {
    if (folderId) {
      return await _getFolder(folderId)
    }

    return await _getDefaultFolder(companyId, 'Images')
  }

  async function _getFolder(folderId: number): Promise<Folder> {
    const data = await $fetch<any>(`v2/folders/${folderId}`, { ...useFetchSharedOptions() })
    return data
  }

  async function _getDefaultFolder(companyId: number, folderName: string): Promise<Folder> {
    const { results: folders } = await $fetch<any>(`v2/companies/${companyId}/shelf/folders`, { ...useFetchSharedOptions() })
    return folders.find((folder: Folder) => folder.name.toLowerCase().includes(folderName.toLowerCase().replace("'", '')))
  }

  return {
    ...toRefs(readonly(state)),
    selectFile,
    setPreviewFile,
    saveFileEdits,
    refetch,
  }
}
